import React from 'react';
import Layout from '../components/Layout';
import PageHero from '../components/PageHero';
import ContactCTA from '../components/ContactCTA';
import FAQ from '../components/FAQ';
import SEO from '../components/SEO';
import { combineSchemas, organizationSchema, serviceSchema, faqSchema, breadcrumbSchema } from '../lib/schema';
import { Instagram, CalendarCheck, Image as ImageIcon, DollarSign, Users, Search, Smartphone, Palette } from 'lucide-react';

const faqs = [
  { q: 'How much does a tattoo shop website cost?', a: 'Custom tattoo studio websites at Website Symphony typically range from $1,800 to $6,500 depending on artist rosters, portfolio depth, online deposit collection and any merch/eCommerce needs. Every project includes a fixed quote up front.' },
  { q: 'Can artists manage their own portfolios?', a: 'Yes. We build tattoo studio sites with an artist login area so each artist can upload new tattoo photos, update their bio, sync their Instagram feed, and manage bookings without needing a developer.' },
  { q: 'Do you handle Instagram integration and booking deposits?', a: 'Every tattoo studio site we build ships with Instagram feed embeds, gallery filtering by style, and Stripe or Square-based deposit collection so no-shows drop and your book stays full.' },
  { q: 'Will my site rank on Google for "tattoo shop near me"?', a: 'Yes. Every tattoo studio site includes local SEO essentials — schema markup, Google Business Profile alignment, style-based landing pages (traditional, realism, black-and-grey), and page-speed optimization to help you rank for high-intent local searches.' },
  { q: 'Can you sell merch, aftercare and gift cards on the site?', a: 'Absolutely. We build tattoo studio sites with Shopify or WooCommerce integration to sell merch, aftercare products, gift cards, and prints — all managed from one dashboard.' },
];

const styles = ['Traditional', 'Neo-Traditional', 'Realism', 'Black & Grey', 'Japanese', 'Fine Line', 'Blackwork', 'Watercolor', 'Geometric', 'Script & Lettering'];

const features = [
  { icon: ImageIcon, title: 'Artist Portfolio Galleries', desc: 'Filterable galleries by artist and style with fullscreen lightbox — built to showcase your best work.' },
  { icon: Instagram, title: 'Instagram Feed Sync', desc: 'Auto-pull each artist\u2019s Instagram feed so your site always shows the freshest work.' },
  { icon: CalendarCheck, title: 'Consultation Booking', desc: 'Custom consultation forms with size, placement, reference upload and preferred artist.' },
  { icon: DollarSign, title: 'Deposit Collection', desc: 'Take non-refundable deposits via Stripe or Square to kill no-shows.' },
  { icon: Users, title: 'Artist Profiles', desc: 'Each artist gets a personal page with bio, style, availability and booking button.' },
  { icon: Search, title: 'Local SEO Built-in', desc: 'Rank for "tattoo shop near me", "[style] tattoo artist [city]" and style-based searches.' },
];

const TattooPage = () => {
  const schema = combineSchemas(
    organizationSchema,
    serviceSchema('Web Design for Tattoo Shops', 'Custom web design for tattoo studios and shops including artist portfolios, Instagram integration, online consultations, deposit collection and local SEO.', '/web-design-for-tattoo-shops'),
    faqSchema(faqs),
    breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Web Design for Tattoo Shops', path: '/web-design-for-tattoo-shops' }]),
  );

  return (
    <Layout>
      <SEO
        title="Web Design for Tattoo Shops | Custom Tattoo Studio Websites | Website Symphony"
        description="Bold, portfolio-driven web design for tattoo shops and studios. Artist galleries, Instagram sync, online deposits, consultation booking and local SEO. Free quote today."
        keywords="web design for tattoo shops, tattoo studio web design, tattoo shop website, tattoo artist website, tattoo parlor web design, custom tattoo studio website"
        canonical="/web-design-for-tattoo-shops"
        schema={schema}
      />
      <PageHero title="Web Design for Tattoo Shops" subtitle="Portfolio-driven websites for tattoo studios — artist galleries, Instagram sync, deposit-backed consultations and local SEO that actually books ink." breadcrumb="Tattoo Shop Web Design" />

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <img src="https://images.unsplash.com/photo-1521123125295-969fabc0da76?w=900&q=80" alt="Tattoo artist working in modern studio" className="rounded-lg shadow-xl w-full h-[440px] object-cover order-2 md:order-1" loading="lazy" />
          <div className="order-1 md:order-2">
            <h2 className="text-[#2C5F66] text-3xl md:text-4xl font-extrabold mb-6">Websites that show off your ink and book your book</h2>
            <p className="text-gray-600 text-[15px] leading-relaxed mb-4">
              Great tattoo shop websites do two things flawlessly: they <strong>showcase the art</strong> and they <strong>collect deposits</strong>. Everything else — pricing, bios, aftercare, merch — supports those two goals.
            </p>
            <p className="text-gray-600 text-[15px] leading-relaxed mb-4">
              At Website Symphony we design tattoo studio websites that feel like walking into your shop — gritty, artistic, unmistakably yours — while quietly qualifying leads, collecting non-refundable deposits, and syncing every artist’s latest work from Instagram.
            </p>
            <p className="text-gray-600 text-[15px] leading-relaxed">Whether you’re a single-artist private studio or a 10-chair street shop, we’ll build a site that ranks for “tattoo shop near me” and turns lookers into deposit-paying clients.</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F7F7F5]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-[#2C5F66] text-3xl md:text-4xl font-extrabold text-center mb-14">Everything a modern tattoo studio website needs</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.title} className="bg-white rounded-lg p-7 shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-full bg-[#2C5F66]/10 flex items-center justify-center mb-4">
                  <f.icon className="text-[#2C5F66]" size={22} />
                </div>
                <h3 className="text-[#2C5F66] font-bold text-lg mb-2">{f.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-[#2C5F66] text-3xl md:text-4xl font-extrabold text-center mb-4">Landing pages for every style you offer</h2>
          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-10">Each tattoo style gets its own SEO-optimized page so clients searching for “realism tattoo artist near me” find you first.</p>
          <div className="flex flex-wrap justify-center gap-3">
            {styles.map((s) => (
              <span key={s} className="px-5 py-2.5 bg-[#2C5F66] text-white rounded-full text-sm font-medium hover:bg-[#D9A04A] transition-colors cursor-default">{s}</span>
            ))}
          </div>
        </div>
      </section>

      <FAQ items={faqs} title="Tattoo Shop Web Design FAQs" />
      <ContactCTA defaultService="Tattoo Shop Web Design" heading="Let’s build a site that books ink" subheading="Get a free tattoo studio website consultation — no obligation, no pressure." />
    </Layout>
  );
};

export default TattooPage;
